import { action, observable, runInAction } from 'mobx';
import isEqual from 'lodash/isEqual';
import { ErrorInterface } from 'modules/core/api/globalApi';
import {
  PartnerListItemInterface,
} from './partnerStoreAPI.d';
import controller from './controller';

export interface PartnerStore {
  partnersList: PartnerListItemInterface[],
  // ключ кэша = последний успешный запрос; null — данных ещё не было
  partnersListRequest: number[] | null,
  partnersListByService: PartnerListItemInterface[],
  partnersListByServiceRequest: number[] | null,

  loadingPartnersList: boolean,
  loadingPartnersListByService: boolean,

  error: ErrorInterface,
}

const initialValues: PartnerStore = {
  partnersList: [],
  partnersListRequest: null,
  partnersListByService: [],
  partnersListByServiceRequest: null,

  loadingPartnersList: false,
  loadingPartnersListByService: false,

  error: null,
};

export const partnerStore = observable(initialValues);

// запрос в полёте (не обзервабл: на рендер не влияет) — single-flight на дублирующие mount'ы
let partnersListInFlight: number[] | null = null;
let partnersListByServiceInFlight: number[] | null = null;

export const partnerActions = {

  handleError: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      partnerStore.error = {
        message: errorData?.message,
        errorCode: errorData?.errorCode,
        timestamp: errorData?.timestamp,
        status: response?.status,
        connectError: response?.connectError,
        isError: response?.error,
      };
      throw errorData;
    }
  }),

  async clearError() {
    partnerStore.error = null;
  },

  // POST /v1/partner/find — список партнёров по массиву serviceIds (ветка serviceIds[] PartnerSelector)
  async getPartnerListByArray(request: number[]) {
    if (isEqual(request, partnerStore.partnersListRequest) || isEqual(request, partnersListInFlight)) {
      return;
    }
    partnersListInFlight = request;
    runInAction(() => {
      // чистим синхронно, чтобы не мигать устаревшим списком при смене params (паттерн OfficeSelector)
      partnerStore.partnersList = [];
      partnerStore.loadingPartnersList = true;
    });
    try {
      const response = await controller.getPartnerListByArray({ serviceIds: request });

      partnerActions.handleError(response);
      runInAction(() => {
        // ключ фиксируем только при успехе: при ошибке следующий mount повторит запрос
        partnerStore.partnersListRequest = request;
        partnerStore.partnersList = response?.data?.items ?? [];
      });
    } catch (err) {
      this.handleError(err);
    } finally {
      partnersListInFlight = null;
      runInAction(() => {
        partnerStore.loadingPartnersList = false;
      });
    }
  },

  // GET /v1/partner?serviceId= — список партнёров по одному сервису/без фильтра (ветка serviceId PartnerSelector).
  // Эндпоинт другой (не /find), поэтому отдельный слот кэша — до подтверждения
  // эквивалентности эндпоинтов бэком (SELECTORS_TSX_PLAN §1.2).
  async getPartnersListByService(serviceIds: number[]) {
    if (isEqual(serviceIds, partnerStore.partnersListByServiceRequest)
      || isEqual(serviceIds, partnersListByServiceInFlight)) {
      return;
    }
    partnersListByServiceInFlight = serviceIds;
    runInAction(() => {
      partnerStore.partnersListByService = [];
      partnerStore.loadingPartnersListByService = true;
    });
    try {
      const serviceId = serviceIds[0];
      const response = await controller.getPartnerList(serviceId === undefined ? {} : { serviceId });

      partnerActions.handleError(response);
      runInAction(() => {
        partnerStore.partnersListByServiceRequest = serviceIds;
        partnerStore.partnersListByService = response?.data ?? [];
      });
    } catch (err) {
      this.handleError(err);
    } finally {
      partnersListByServiceInFlight = null;
      runInAction(() => {
        partnerStore.loadingPartnersListByService = false;
      });
    }
  },

  clearPartnerList: action(() => {
    partnerStore.partnersList = [];
    partnerStore.partnersListRequest = null;
    partnerStore.partnersListByService = [];
    partnerStore.partnersListByServiceRequest = null;
    partnerStore.error = null;
  }),

};
